import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtCurrency } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Plus, Building2, Trash2, ArrowUpRight, Lock, Zap } from "lucide-react";
import { toast } from "sonner";

export default function DashboardPage() {
  const t = useT();
  const { user } = useAuth();
  const isTrial = user?.tier === "TRIAL";
  const isFree = user?.tier === "FREE";
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", location: "", sport: "Zeilen", description: "" });
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clubs");
      setClubs(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createClub = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/clubs", form);
      toast.success(t("Club aangemaakt"));
      setOpenNew(false);
      setForm({ name: "", location: "", sport: "Zeilen", description: "" });
      nav(`/clubs/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteClub = async (id) => {
    if (!window.confirm(t("Deze club en alle bijbehorende gegevens verwijderen?"))) return;
    try {
      await api.delete(`/clubs/${id}`);
      toast.success(t("Club verwijderd"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <AppLayout>
      {isTrial && user?.trial_ends_at && (
        <TrialCountdownBanner endsAt={user.trial_ends_at} t={t} />
      )}
      {isFree && (
        <div className="mb-6 border-2 border-destructive bg-destructive/10 p-4 flex items-start gap-3" data-testid="trial-expired-banner">
          <Lock className="w-5 h-5 text-destructive mt-0.5" />
          <div className="flex-1">
            <p className="font-display uppercase font-bold text-sm text-destructive">{t("Proefperiode verlopen")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("Kies een abonnement om verder te werken met je scenario's en data.")}</p>
          </div>
          <Link to="/prijzen"
             data-testid="trial-expired-cta"
             className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-xs">
            {t("Bekijk abonnementen")}
          </Link>
        </div>
      )}
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="stat-label">{t("Portfolio")}</div>
          <h1 className="font-display font-black text-5xl md:text-6xl uppercase tracking-tighter mt-1">{t("Jouw Clubs")}</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">{t("Maak de begroting voor het volgend jaar, samen met een meerjarenbegroting, en modelleer langetermijninvesteringen in faciliteiten, gebouwen, banen — en stresstest meerdere scenario's.")}</p>
        </div>
        <button
          onClick={() => (isTrial || isFree
              ? nav("/prijzen")
              : setOpenNew(true))}
          data-testid="new-club-btn"
          disabled={false}
          title={isTrial ? t("Proef-abonnement: alleen de demo-club. Upgrade naar STANDARD/PREMIUM.") : isFree ? t("Proef verlopen — kies een abonnement.") : ""}
          className={`btn-brutal px-5 py-3 font-display uppercase font-black tracking-wider inline-flex items-center gap-2 ${(isTrial || isFree) ? "bg-muted text-muted-foreground opacity-70" : "bg-primary text-primary-foreground"}`}
        >
          {isTrial || isFree ? <Zap className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {isTrial ? t("Upgrade om club toe te voegen") : isFree ? t("Upgrade voor toegang") : t("Nieuwe Club")}
        </button>
      </div>

      {openNew && (
        <form onSubmit={createClub} className="mb-8 bg-card border-2 border-foreground p-6 heavy-shadow" data-testid="new-club-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="stat-label">{t("Naam van de club")}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-club-name"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="stat-label">{t("Sport")}</label>
              <input value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })}
                data-testid="new-club-sport"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="stat-label">{t("Locatie")}</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="new-club-location"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="stat-label">{t("Omschrijving")}</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="new-club-desc"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button type="submit" data-testid="new-club-submit"
              className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold">{t("Aanmaken")}</button>
            <button type="button" onClick={() => setOpenNew(false)}
              className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold">{t("Annuleren")}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="stat-label">{t("Laden…")}</div>
      ) : clubs.length === 0 ? (
        <div className="border-2 border-dashed border-foreground/40 p-10 text-center">
          <Building2 className="w-10 h-10 mx-auto text-muted-foreground" />
          <div className="mt-3 font-display uppercase font-bold text-xl">{t("Nog geen clubs")}</div>
          <p className="text-muted-foreground mt-1">{t("Maak je eerste club aan om investeringsscenario's te bouwen.")}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="clubs-grid">
          {clubs.map((c) => {
            const isDemoLocked = (isTrial || isFree) && c.is_demo;
            const isShared = Boolean(c.shared_role);
            const deleteDisabled = c.is_locked || isDemoLocked || isShared;
            const deleteTitle = isShared
              ? t("Alleen de eigenaar kan deze club verwijderen.")
              : isDemoLocked
                ? t("Demo-club kan tijdens de proef niet verwijderd worden.")
                : c.is_locked
                  ? t("Ontgrendel eerst om te verwijderen.")
                  : t("Verwijderen");
            return (
            <div key={c.id} className="bg-card border-2 border-foreground p-6 heavy-shadow hover:heavy-shadow-primary transition-shadow group">
              <div className="flex items-start justify-between">
                <div>
                  <div className="stat-label flex items-center gap-2 flex-wrap">
                    {c.sport || "Sport"}
                    {c.is_demo && (
                      <span className="inline-flex items-center gap-1 bg-primary text-primary-foreground px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest" data-testid={`club-demo-badge-${c.id}`} title={t("Voorbeelddata voor de 14-dagen proef.")}>
                        <Zap className="w-2.5 h-2.5" /> {t("Demo")}
                      </span>
                    )}
                    {c.is_locked && (
                      <span className="inline-flex items-center gap-1 bg-destructive text-destructive-foreground px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest" data-testid={`club-locked-badge-${c.id}`} title={t("Deze club is vergrendeld.")}>
                        <Lock className="w-2.5 h-2.5" /> {t("Vergrendeld")}
                      </span>
                    )}
                    {c.shared_role && (
                      <span
                        className="inline-flex items-center gap-1 bg-accent text-accent-foreground border-2 border-foreground px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest"
                        data-testid={`club-shared-badge-${c.id}`}
                        title={t("Gedeeld door de eigenaar — jouw rol: {role}").replace("{role}", c.shared_role)}
                      >
                        {t("Gedeeld")} · {c.shared_role === "editor" ? t("Editor") : t("Viewer")}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-black text-3xl uppercase tracking-tight mt-1 leading-tight">{c.name}</div>
                </div>
                <button onClick={() => !deleteDisabled && deleteClub(c.id)} data-testid={`delete-club-${c.id}`}
                  disabled={deleteDisabled}
                  title={deleteTitle}
                  className={`p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors ${deleteDisabled ? "cursor-not-allowed opacity-30" : ""}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{c.location || "—"}</p>
              {c.description && <p className="text-sm mt-3">{c.description}</p>}
              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                <span className="stat-label">{t("Open werkruimte")}</span>
                <Link to={`/clubs/${c.id}`} data-testid={`open-club-${c.id}`}
                  className="btn-brutal bg-secondary text-secondary-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-1">
                  {t("Openen")} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}

function TrialCountdownBanner({ endsAt, t }) {
  const daysLeft = Math.max(0, Math.ceil((new Date(endsAt) - new Date()) / (1000 * 60 * 60 * 24)));
  const urgent = daysLeft <= 3;
  return (
    <div className={`mb-6 border-2 ${urgent ? "border-destructive bg-destructive/10" : "border-foreground bg-accent/20"} p-4 flex items-start gap-3 flex-wrap`} data-testid="trial-countdown-banner">
      <div className={`p-1.5 border-2 border-foreground ${urgent ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}>
        <Zap className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className="font-display uppercase font-bold text-sm">
          {t("Proefperiode actief")} · {daysLeft === 1 ? t("nog 1 dag") : `${t("nog")} ${daysLeft} ${t("dagen")}`}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("Je werkt op de demo-club. Upgrade om eigen clubs toe te voegen en verder te werken na de proef.")}
        </p>
      </div>
      <Link to="/prijzen"
         data-testid="trial-upgrade-cta"
         className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-xs">
        {t("Bekijk abonnementen")}
      </Link>
    </div>
  );
}
